import { IconButton, Input } from '@chakra-ui/react';
import { ATTACHMENT_ACCEPT_TYPES } from '@bower/shared/schemas/upload.schema';
import { ACTION_ROW_GLYPH_SIZE } from '../../ui/iconButtonMetrics';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { useRef } from 'react';

import { pickAttachments } from './composerFiles';

interface AttachmentButtonProps {
  onFilesSelected: (files: File[]) => void;
  onFilesRejected?: (files: File[], errors: string[]) => void;
}

function AttachmentButton({ onFilesSelected, onFilesRejected }: AttachmentButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    // The size + type gate lives in `composerFiles`, shared with the drop and
    // paste paths — this button is one of three ways into the same tray, and
    // three copies of the rule is three chances for them to disagree.
    const { accepted, rejected, errors } = pickAttachments(Array.from(selectedFiles));

    if (accepted.length > 0) {
      onFilesSelected(accepted);
    }

    if (rejected.length > 0 && onFilesRejected) {
      onFilesRejected(rejected, errors);
    }

    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Extensions only, from the shared accept map: the native picker's `accept`
  // filters by suffix reliably, whereas MIME keys silently hide the very files
  // the OS has no MIME type for.
  const acceptString = Object.values(ATTACHMENT_ACCEPT_TYPES).flat().join(',');

  return (
    <>
      <Input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptString}
        onChange={handleFileChange}
        display="none"
      />
      <IconButton
        aria-label="Attach files"
        // Stock Material attach icon, on the shared composer glyph size.
        icon={<MaterialIcon name="attach_file" boxSize={ACTION_ROW_GLYPH_SIZE} />}
        variant="ghost"
        size="md"
        color="text.muted"
        _hover={{ color: 'text.secondary', bg: 'interactive.hover' }}
        onClick={handleButtonClick}
      />
    </>
  );
}

export default AttachmentButton;
